import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import {
  QcComment,
  QcIssue,
  QcIssueAuthor,
  QcIssueCategory,
  QcIssueStatus,
  QC_ISSUE_STATUS_LABELS,
} from '../../../interfaces/qcIssue.interface';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { QcFirstPassNote } from '../../../interfaces/qcIssue.interface';
import { QC_STATUS_RGB, ACCENT_BLUE, SUCCESS_GREEN, ERROR_RED, WARN_ORANGE, FIRST_PASS_PURPLE } from './qcTheme';
import { QC_GROUP_ORDER, OrderedQuestionEntry, WorkspaceCapabilities } from './workspaceDerivations';
import QcIssueCard from './QcIssueCard';
import AddQcIssueModal from './AddQcIssueModal';
import QcCommentsModal from './QcCommentsModal';

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '2px',
});

const Caption = styled('p')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  margin: 0,
});

const AddIssueButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  flex: 'none',
  background: '#fff',
  color: ERROR_RED,
  border: '1px solid rgba(198,40,40,0.5)',
  borderRadius: '6px',
  padding: '9px 16px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: 'rgba(198,40,40,0.06)',
  },
});

// First Pass rollup note ("No issue raised" + reasoning): admin-only, shown only when the question carries no QC issues.
const NoteBox = styled('div')({
  border: '1px solid rgba(94,53,177,0.3)',
  background: 'rgba(94,53,177,0.05)',
  borderRadius: '8px',
  padding: '14px 16px',
  marginTop: '4px',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
});

const NoteTitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  marginBottom: '8px',
});

const NoteTitle = styled('span')({
  fontSize: '0.85rem',
  fontWeight: 600,
  color: FIRST_PASS_PURPLE,
  '& .MuiSvgIcon-root': {
    fontSize: '15px',
    verticalAlign: '-2px',
    marginRight: '5px',
  },
});

const CertaintyChip = styled('span')({
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  height: '22px',
  padding: '0 10px',
  borderRadius: '11px',
  border: '1px solid rgba(94,53,177,0.3)',
  background: '#fff',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: FIRST_PASS_PURPLE,
  whiteSpace: 'nowrap',
});

const NoteText = styled('p')({
  margin: '0 0 12px',
  fontSize: '0.875rem',
  lineHeight: 1.6,
  color: 'rgba(0,0,0,0.87)',
});

const NoteObservations = styled('ul')({
  margin: '0 0 12px',
  padding: '0 0 0 18px',
  fontSize: '0.8rem',
  lineHeight: 1.6,
  color: 'rgba(0,0,0,0.6)',
});

const NoteQuestionRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
});

const NoteQuestionLabel = styled('span')({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.7)',
});

const NoteAction = styled('button', {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: 'agree' | 'flag' }>(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '30px',
  padding: '0 12px',
  borderRadius: '4px',
  border: `1px solid ${tone === 'agree' ? SUCCESS_GREEN : ERROR_RED}`,
  background: '#fff',
  color: tone === 'agree' ? SUCCESS_GREEN : ERROR_RED,
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '& .MuiSvgIcon-root': { fontSize: '14px' },
  '&:hover': {
    background: tone === 'agree' ? 'rgba(46,125,50,0.06)' : 'rgba(198,40,40,0.06)',
  },
}));

const NoteFeedbackArea = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '64px',
  marginTop: '10px',
  border: '1px solid rgba(0,0,0,0.3)',
  borderRadius: '6px',
  padding: '8px 10px',
  fontFamily: 'inherit',
  fontSize: '0.85rem',
  color: 'rgba(0,0,0,0.87)',
  background: '#fff',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.18)',
  },
});

const FeedbackActionsRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  marginTop: '6px',
});

const FeedbackCancelButton = styled('button')({
  background: '#fff',
  color: 'rgba(0,0,0,0.7)',
  border: 'none',
  borderRadius: '4px',
  padding: '6px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: 'rgba(0,0,0,0.04)' },
});

const FeedbackSubmitButton = styled('button')({
  background: FIRST_PASS_PURPLE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '6px 14px',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: '#512da8' },
});

const NoteResolved = styled('p', {
  shouldForwardProp: prop => prop !== 'accepted',
})<{ accepted: boolean }>(({ accepted }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  margin: '12px 0 0',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: accepted ? SUCCESS_GREEN : WARN_ORANGE,
  '& .MuiSvgIcon-root': { fontSize: '15px' },
}));

const PanelRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

// Anchored near the top with fixed padding — centering in a tall column floated unpredictably.
const EmptyState = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '40px 16px',
  color: 'rgba(0,0,0,0.4)',
  textAlign: 'center',
  fontSize: '0.875rem',
});

const EmptyIcon = styled(VerifiedIcon)({
  fontSize: '34px',
  color: 'rgba(0,0,0,0.18)',
});

const Group = styled('div')({
  marginTop: '18px',
});

const GroupHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '10px',
});

// muted mirrors the Not Applicable answer chip's grays so the adjacent panels agree.
const StatusPill = styled('span', {
  shouldForwardProp: prop => !['rgb', 'muted'].includes(prop as string),
})<{ rgb: string; muted?: boolean }>(({ rgb, muted }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  height: '30px',
  padding: '0 16px',
  borderRadius: '15px',
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.2px',
  whiteSpace: 'nowrap',
  flex: 'none',
  background: muted ? 'rgba(97,97,97,0.07)' : `rgba(${rgb},0.14)`,
  color: muted ? '#616161' : `rgb(${rgb})`,
}));

const CommentsFooter = styled('div')({
  marginTop: '20px',
});

const CommentsRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
});

const CommentsButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#fff',
  border: '1px solid rgba(25,118,210,0.5)',
  color: ACCENT_BLUE,
  borderRadius: '4px',
  padding: '6px 12px',
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    borderColor: ACCENT_BLUE,
    background: 'rgba(25,118,210,0.04)',
  },
});

const STATUS_ICONS: Record<QcIssueStatus, React.ReactElement> = {
  first_pass: <AutoAwesomeIcon sx={{ fontSize: 16 }} />,
  pending_assessor_action: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
  ready_for_tpn_review: <SendIcon sx={{ fontSize: 16 }} />,
  approved: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  needs_update: <UndoIcon sx={{ fontSize: 16 }} />,
};

export interface QcReviewPanelProps {
  issues: QcIssue[];
  comments: QcComment[];
  entries: OrderedQuestionEntry[];
  questionId: number;
  questionCode: string;
  capabilities: WorkspaceCapabilities;
  qcStageVisible: boolean;
  qcReviewSubmitted: boolean;
  currentAuthor: QcIssueAuthor;
  currentRound?: number;
  note?: QcFirstPassNote;
  onSetStatus: (issueId: number, status: QcIssueStatus) => void;
  onNavigateToCode: (shortCode: string) => void;
  onNoteResponse?: (adminResponse: 'accept' | 'reject', noteText?: string) => void;
  onPostQcComment: (text: string) => void;
  onAddQcIssue: (category: QcIssueCategory, text: string) => void;
}

export default function QcReviewPanel(props: QcReviewPanelProps): JSX.Element {
  const {
    issues, comments, entries, questionId, questionCode, capabilities,
    qcStageVisible, qcReviewSubmitted, currentAuthor, currentRound, note, onSetStatus, onNavigateToCode,
    onNoteResponse, onPostQcComment, onAddQcIssue,
  } = props;
  const [addOpen, setAddOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [noteFeedbackOpen, setNoteFeedbackOpen] = useState(false);
  const [noteFeedback, setNoteFeedback] = useState('');

  const visibleIssues = capabilities.firstPassVisible ? issues : issues.filter(i => i.status !== 'first_pass');
  // Hidden whenever the question has any QC issue (PRD rule) — a drafting-stage artifact, admin-only, gone once submitted.
  const showNote = capabilities.firstPassVisible && !qcReviewSubmitted && !visibleIssues.length && !!note;
  // not_evaluated = First Pass never made a call; its stored rationale is pipeline internals, so show plain copy.
  const noteNotEvaluated = note?.verdict === 'not_evaluated';
  const noteBody = noteNotEvaluated
    ? 'First Pass could not complete an automated review of this response, so no issue was raised. '
      + 'Review the response manually and add a QC issue if one is needed.'
    : note?.rationale;

  const handleAddIssue = (category: QcIssueCategory, text: string) => {
    onAddQcIssue(category, text);
    setAddOpen(false);
  };

  return (
    <PanelRoot>
      <HeaderRow>
        <Caption>QC Issues ({visibleIssues.length})</Caption>
        {capabilities.addQcIssue && (
          <AddIssueButton type='button' onClick={() => setAddOpen(true)}>
            <AddIcon sx={{ fontSize: 18 }} />
            QC Issue
          </AddIssueButton>
        )}
      </HeaderRow>
      {showNote && note && (
        <NoteBox>
          <NoteTitleRow>
            <NoteTitle>
              <AutoAwesomeIcon />
              First Pass · No issue raised
            </NoteTitle>
            {note.confidence !== undefined && (
              <CertaintyChip>{Math.round(note.confidence * 100)}% certain</CertaintyChip>
            )}
          </NoteTitleRow>
          <NoteText>{noteBody}</NoteText>
          {!!(note.crossReferences?.length || note.spellingNotes?.length) && (
            <NoteObservations>
              {note.crossReferences?.map(item => <li key={item}>{item}</li>)}
              {note.spellingNotes?.map(item => <li key={item}>Spelling: {item}</li>)}
            </NoteObservations>
          )}
          {!note.adminResponse && onNoteResponse && !noteFeedbackOpen && (
            <NoteQuestionRow>
              <NoteQuestionLabel>Was this call right?</NoteQuestionLabel>
              <NoteAction tone='agree' type='button' onClick={() => onNoteResponse('accept')}>
                <ThumbUpIcon />
                Agree
              </NoteAction>
              <NoteAction tone='flag' type='button' onClick={() => setNoteFeedbackOpen(true)}>
                <ThumbDownIcon />
                Should have raised an issue
              </NoteAction>
            </NoteQuestionRow>
          )}
          {!note.adminResponse && onNoteResponse && noteFeedbackOpen && (
            <>
              <NoteFeedbackArea
                value={noteFeedback}
                placeholder='What should First Pass have caught? (optional — trains First Pass)'
                onChange={event => setNoteFeedback(event.target.value)}
              />
              <FeedbackActionsRow>
                <FeedbackCancelButton
                  type='button'
                  onClick={() => { setNoteFeedbackOpen(false); setNoteFeedback(''); }}
                >
                  Cancel
                </FeedbackCancelButton>
                <FeedbackSubmitButton
                  type='button'
                  onClick={() => {
                    onNoteResponse('reject', noteFeedback.trim());
                    setNoteFeedbackOpen(false);
                    setNoteFeedback('');
                  }}
                >
                  Submit Feedback
                </FeedbackSubmitButton>
              </FeedbackActionsRow>
            </>
          )}
          {note.adminResponse && (
            <NoteResolved accepted={note.adminResponse === 'accept'}>
              <CheckCircleIcon />
              {note.adminResponse === 'accept'
                ? 'Feedback recorded — confirmed as correct'
                : 'Marked as a missed issue — feeds First Pass training'}
            </NoteResolved>
          )}
        </NoteBox>
      )}
      {!visibleIssues.length && !showNote && (
        <EmptyState>
          <EmptyIcon />
          {qcStageVisible
            ? 'No QC issues raised on this response.'
            : 'No QC issues yet — QC review begins after this assessment is submitted for TPN Review.'}
        </EmptyState>
      )}
      {QC_GROUP_ORDER.map(status => {
        const group = visibleIssues.filter(issue => issue.status === status);
        if (!group.length) return null;
        return (
          <Group key={status}>
            <GroupHead>
              <StatusPill rgb={QC_STATUS_RGB[status]} muted={status === 'pending_assessor_action'}>
                {STATUS_ICONS[status]}
                {QC_ISSUE_STATUS_LABELS[status]}
              </StatusPill>
            </GroupHead>
            {group.map(issue => (
              <QcIssueCard
                key={issue.id}
                issue={issue}
                questionId={questionId}
                entries={entries}
                capabilities={capabilities}
                qcReviewSubmitted={qcReviewSubmitted}
                currentAuthor={currentAuthor}
                currentRound={currentRound}
                onSetStatus={onSetStatus}
                onNavigateToCode={onNavigateToCode}
              />
            ))}
          </Group>
        );
      })}
      {qcStageVisible && (
        <CommentsFooter>
          <CommentsRow>
            <CommentsButton type='button' onClick={() => setCommentsOpen(true)}>
              {comments.length
                ? <ChatBubbleIcon sx={{ fontSize: 16 }} />
                : <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
              <span>Comments&nbsp;({comments.length})</span>
            </CommentsButton>
          </CommentsRow>
        </CommentsFooter>
      )}
      <AddQcIssueModal
        open={addOpen}
        questionCode={questionCode}
        entries={entries}
        onAdd={handleAddIssue}
        onClose={() => setAddOpen(false)}
      />
      <QcCommentsModal
        open={commentsOpen}
        questionCode={questionCode}
        comments={comments}
        entries={entries}
        onPost={onPostQcComment}
        onClose={() => setCommentsOpen(false)}
      />
    </PanelRoot>
  );
}
